import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import {
  RequestLoadConsents, ReceiveLoadConsents, FailLoadConsents,
  RequestDeleteConsent, ReceiveDeleteConsent,
  FailDeleteConsent, ReceiveLoadConsent, FailLoadConsent, RequestLoadConsent, ClearConsent,
  RequestLoadPermission, ReceiveLoadPermission,
  FailLoadPermission, ProcessConsentFailed, ClearValidation, RequestSaveSubscription,
  ReceiveSaveSubscription, FailSaveSubscription, RequestVerifyCode, ReceiveVerifyCode, FailVerifyCode
} from 'app/area/user-profile/my-notifications/action-types';
import { api } from './api';
import { createDescriptionSelector } from 'app/infrastructure/translate';
import { myNotificationsTranslations } from 'app/area/user-profile/my-notifications/constants';
import { ConsentPermission, Subscription } from 'app/area/user-profile/my-notifications/types';
import { AnyAction } from 'redux';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { ConsentType } from 'app/area/user-profile/my-notifications/types';

export const actionCreators = {
  loadPermission: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new RequestLoadPermission());

      try {
        const response: ConsentPermission = await api.loadPermission();
        const userMessages = response?.messages?.userMessages ?? [];

        userMessages && userMessages.forEach(message => dispatch(new ErrorNotification([message])));
        dispatch(new ReceiveLoadPermission(response));
      } catch {
        dispatch(new FailLoadPermission());
      }
    },

  loadConsents: (recipientId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new RequestLoadConsents());

      try {
        const response = await api.loadConsents(recipientId);
        dispatch(new ReceiveLoadConsents(response));
      } catch {
        dispatch(new FailLoadConsents());
      }
    },

  loadConsent: (id: number, recipientId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new RequestLoadConsent());

      try {
        const response = await api.loadConsent(id, recipientId);
        dispatch(new ReceiveLoadConsent(response));
      } catch {
        dispatch(new FailLoadConsent());
      }
    },

  deleteConsent: (id: number, recipientId: number, onSuccess: () => void): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const areaState = getState().myNotifications;
      const translationDescription = createDescriptionSelector(areaState.texts);

      dispatch(new RequestDeleteConsent());

      try {
        await api.deleteConsent(id, recipientId);
        dispatch(new ReceiveDeleteConsent());
        onSuccess();
        dispatch(new SuccessNotification(translationDescription(myNotificationsTranslations.consent_delete_success)));
      } catch (err) {
        dispatch(new FailDeleteConsent());
        dispatch(new ErrorNotification(err.userMessages || err.message));
      }
    },

  verifyCode: (subscription: Subscription, onSuccess: () => void): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const areaState = getState().myNotifications;
      const translationDescription = createDescriptionSelector(areaState.texts);

      dispatch(new RequestVerifyCode());

      try {
        await api.verifyCode(subscription);
        dispatch(new ReceiveVerifyCode());
        onSuccess();
        dispatch(new SuccessNotification(translationDescription(myNotificationsTranslations.verification_code_success)));
      } catch (err) {
        dispatch(new FailVerifyCode());
        dispatch(new ProcessConsentFailed(err.validationErrors ? complexValidationFromServer(err.validationErrors) : []));
      }
    },

  saveSubscription: (id: number, subscription: Subscription, onSuccess: () => void): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const areaState = getState().myNotifications;
      const translationDescription = createDescriptionSelector(areaState.texts);

      dispatch(new RequestSaveSubscription());

      try {
        const result = (id || id === 0)
          ? await api.updateSubscription(id, subscription)
          : await api.addSubscription(subscription);

        if (result) {
          dispatch(new ReceiveSaveSubscription());
          onSuccess();

          const translation = {
            [ConsentType.sms]: {
              add: myNotificationsTranslations.consent_sms_add_success,
              edit: myNotificationsTranslations.consent_sms_update_success
            }
          }[subscription.type];

          dispatch(new SuccessNotification(translationDescription(id ? translation.edit : translation.add)));
        }
      } catch (err) {
        dispatch(new FailSaveSubscription());
        dispatch(new ProcessConsentFailed(err.validationErrors ? complexValidationFromServer(err.validationErrors) : []));
      }
    },

  clearConsent: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearConsent());
    },

  clearValidation: (formName: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearValidation(formName));
    }
};
